import { useState, useEffect } from 'react';
import { Layout } from '../../../components/layout/Layout';
import api from '../../../api';
import { Input } from '../../../components/ui/input';
import { Card, CardContent } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import {
  Search, HardDrive, Filter, X, AlertTriangle, Monitor,
  ChevronLeft, ChevronRight, ChevronUp, ChevronDown, ChevronsUpDown
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { toast } from 'sonner';
import { formatDateARG } from '../../../lib/utils';

const formatSize = (gb) => {
  if (gb == null) return '-';
  const n = Number(gb);
  if (n >= 1024) return `${(n / 1024).toFixed(1)} TB`;
  return `${n.toFixed(1)} GB`;
};

const HypervVhds = () => {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, page_size: 25, total: 0, pages: 1 });
  const [filters, setFilters] = useState({ search: '', vhd_format: '', orphan_only: '' });
  const [sortConfig, setSortConfig] = useState({ field: 'size_gb', dir: 'desc' });
  const [activeStatFilter, setActiveStatFilter] = useState('all');

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        page_size: pagination.page_size.toString(),
        sort_by: sortConfig.field,
        sort_dir: sortConfig.dir,
      });
      if (filters.search) params.append('search', filters.search);
      if (filters.vhd_format) params.append('vhd_format', filters.vhd_format);
      if (filters.orphan_only === 'yes') params.append('orphan_only', 'true');
      const res = await api.get(`/virt/hyperv/vhds?${params}`);
      setItems(res.data.items);
      setPagination(prev => ({ ...prev, total: res.data.total, pages: res.data.pages }));
    } catch (err) {
      toast.error('Error al cargar discos VHD');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => { try { setStats((await api.get('/virt/hyperv/vhds/stats')).data); } catch (e) { console.error(e); } };

  useEffect(() => { fetchStats(); }, []);
  useEffect(() => {
    const t = setTimeout(fetchData, 300);
    return () => clearTimeout(t);
  }, [pagination.page, filters, sortConfig]);

  const handleSort = (field) => setSortConfig(prev => ({ field, dir: prev.field === field && prev.dir === 'asc' ? 'desc' : 'asc' }));
  const SortIcon = ({ field }) => sortConfig.field !== field
    ? <ChevronsUpDown className="h-3 w-3 text-muted-foreground/50" />
    : (sortConfig.dir === 'asc' ? <ChevronUp className="h-3 w-3 text-primary" /> : <ChevronDown className="h-3 w-3 text-primary" />);
  const SortableHeader = ({ field, children, className = '' }) => (
    <th className={`cursor-pointer hover:bg-muted/70 select-none ${className}`} onClick={() => handleSort(field)}>
      <div className="flex items-center gap-1">{children}<SortIcon field={field} /></div>
    </th>
  );

  const handleStatFilter = (t) => {
    setActiveStatFilter(t);
    setPagination(prev => ({ ...prev, page: 1 }));
    setFilters({ search: '', vhd_format: '', orphan_only: t === 'orphans' ? 'yes' : '' });
  };

  const pathName = (p) => { if (!p) return '-'; const parts = String(p).split(/[\\/]/); return parts[parts.length - 1] || p; };
  const clearFilters = () => { setFilters({ search: '', vhd_format: '', orphan_only: '' }); setActiveStatFilter('all'); };
  const hasFilters = filters.search || filters.vhd_format || filters.orphan_only;

  return (
    <Layout>
      <div className="space-y-6">
        <div className="page-header">
          <h1 className="page-title">Discos VHD / VHDX</h1>
          <p className="page-subtitle">Discos virtuales Hyper-V. Los huérfanos no están referenciados por ninguna VM.</p>
        </div>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className={`card-subtle cursor-pointer transition-all hover:border-primary/50 ${activeStatFilter === 'all' ? 'border-primary ring-1 ring-primary/20' : ''}`} onClick={() => handleStatFilter('all')} data-testid="stat-total"><CardContent className="p-4 text-center"><p className="stats-card-value">{stats.total || 0}</p><p className="stats-card-label">Total discos</p></CardContent></Card>
            <Card className={`card-subtle cursor-pointer transition-all hover:border-orange-500/50 ${activeStatFilter === 'orphans' ? 'border-orange-500 ring-1 ring-orange-500/20' : ''}`} onClick={() => handleStatFilter('orphans')} data-testid="stat-orphans"><CardContent className="p-4 text-center"><p className="stats-card-value text-orange-500">{stats.orphans || 0}</p><p className="stats-card-label">Huérfanos</p></CardContent></Card>
            <Card className="card-subtle"><CardContent className="p-4 text-center"><p className="stats-card-value">{formatSize(stats.total_file_size_gb)}</p><p className="stats-card-label">Espacio en disco</p></CardContent></Card>
            <Card className="card-subtle"><CardContent className="p-4 text-center"><p className="stats-card-value text-orange-500">{formatSize(stats.wasted_gb)}</p><p className="stats-card-label">Desperdiciado</p></CardContent></Card>
          </div>
        )}

        <Card className="card-subtle">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2"><Filter className="h-4 w-4 text-muted-foreground" /><span className="text-sm font-medium">Filtros:</span></div>
              <div className="flex items-center gap-2 flex-1 min-w-[200px] max-w-md">
                <Search className="h-4 w-4 text-muted-foreground" />
                <Input placeholder="Buscar por ruta o VM..." value={filters.search}
                  onChange={(e) => { setFilters(prev => ({ ...prev, search: e.target.value })); setActiveStatFilter(''); }}
                  className="h-9" data-testid="vhd-search-input" />
              </div>
              <Select value={filters.vhd_format || 'todos'} onValueChange={(v) => { setFilters(prev => ({ ...prev, vhd_format: v === 'todos' ? '' : v })); setActiveStatFilter(''); }}>
                <SelectTrigger className="w-32 h-9" data-testid="vhd-format-filter"><SelectValue placeholder="Formato" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="vhdx">VHDX</SelectItem>
                  <SelectItem value="vhd">VHD</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filters.orphan_only || 'todos'} onValueChange={(v) => { setFilters(prev => ({ ...prev, orphan_only: v === 'todos' ? '' : v })); setActiveStatFilter(''); }}>
                <SelectTrigger className="w-40 h-9" data-testid="vhd-orphan-filter"><SelectValue placeholder="Huérfanos" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="yes">Solo huérfanos</SelectItem>
                </SelectContent>
              </Select>
              {hasFilters && <Button variant="ghost" size="sm" onClick={clearFilters}><X className="h-4 w-4 mr-1" /> Limpiar</Button>}
            </div>
          </CardContent>
        </Card>

        <Card className="card-subtle">
          <CardContent className="p-0">
            <table className="table-subtle" data-testid="vhds-table">
              <thead className="bg-muted/50">
                <tr className="border-b">
                  <SortableHeader field="path" className="text-left">ARCHIVO</SortableHeader>
                  <SortableHeader field="vm_name" className="text-left">VM</SortableHeader>
                  <SortableHeader field="vhd_format" className="text-center justify-center">FORMATO</SortableHeader>
                  <SortableHeader field="vhd_type" className="text-left">TIPO</SortableHeader>
                  <SortableHeader field="size_gb" className="text-center justify-center">TAMAÑO LÓGICO</SortableHeader>
                  <SortableHeader field="file_size_gb" className="text-center justify-center">EN DISCO</SortableHeader>
                  <SortableHeader field="is_orphan" className="text-center justify-center">ESTADO</SortableHeader>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={7} className="text-center p-8 text-muted-foreground">Cargando...</td></tr>
                ) : items.length === 0 ? (
                  <tr><td colSpan={7} className="text-center p-8 text-muted-foreground">No hay discos registrados</td></tr>
                ) : items.map(vh => (
                  <tr key={vh.id} className="border-b hover:bg-muted/30" data-testid={`vhd-row-${vh.id}`}>
                    <td className="p-4">
                      <div className="flex items-center gap-2"><HardDrive className="h-4 w-4 text-muted-foreground" /><span className="font-medium">{pathName(vh.path)}</span></div>
                      <p className="text-xs text-muted-foreground ml-6 font-mono truncate max-w-md" title={vh.path}>{vh.path}</p>
                    </td>
                    <td className="p-4 text-sm">{vh.vm_name ? <div className="flex items-center gap-1"><Monitor className="h-3 w-3 text-muted-foreground" />{vh.vm_name}</div> : <span className="text-muted-foreground">-</span>}</td>
                    <td className="p-4 text-center"><Badge variant="outline" className="uppercase">{vh.vhd_format || '-'}</Badge></td>
                    <td className="p-4 text-sm text-muted-foreground">{vh.vhd_type || '-'}</td>
                    <td className="p-4 text-center">{formatSize(vh.size_gb)}</td>
                    <td className="p-4 text-center">{formatSize(vh.file_size_gb)}</td>
                    <td className="p-4 text-center">
                      {vh.is_orphan ? (
                        <Badge className="bg-orange-500 text-white"><AlertTriangle className="h-3 w-3 mr-1" />Huérfano</Badge>
                      ) : (
                        <Badge variant="outline" className="text-green-500 border-green-500">En uso</Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>

        {pagination.pages > 1 && (
          <div className="flex justify-center items-center gap-4">
            <Button variant="outline" size="sm" disabled={pagination.page === 1} onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}><ChevronLeft className="h-4 w-4" /></Button>
            <span className="text-sm text-muted-foreground">Página {pagination.page} de {pagination.pages} ({pagination.total} discos)</span>
            <Button variant="outline" size="sm" disabled={pagination.page === pagination.pages} onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}><ChevronRight className="h-4 w-4" /></Button>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default HypervVhds;
